import { ProductConstants } from '../../../constants/ProductConstants';

export interface GuidanceCardQuestion {
    label: string;
    query?: string;
}

// Defines the structure for a single accordion section
export interface GuidanceCardSection {
    title: string;
    body: string;
    questions: GuidanceCardQuestion[];
    link?: {
        text: string;
        url: string;
    };
}

// Defines the structure for the entire card for one product
export interface GuidanceCardProductContent {
    title: string;
    description: string;
    link?: {
        text: string;
        url: string;
    };
    sections: GuidanceCardSection[];
}

export interface GuidanceCardContentMap {
    default: GuidanceCardProductContent;
    [key: string]: GuidanceCardProductContent;
}

const getEnEAccountingContent = (businessType: string): GuidanceCardProductContent => {
    const resultLabel = "How do I post the year's result?";
    let resultQuery = "How do I post the year's result in Spiris?";

    if (businessType === 'SoleProprietorship') {
        resultQuery = 'Post the annual result for a sole proprietorship in Spiris';
    } else if (businessType === 'Corporation') {
        resultQuery = 'Post the annual result for a limited liability company in Spiris';
    }

    return {
        title: 'Year-End Reconciliation Checklist',
        description:
            'This is a period when many people are working on their year-end closing. Below are some common questions. [Read more here.](https://support.spiris.se/bokforing-fakturering/en-se/content/online-help/accounting-year-end-closing-checklist-se.htm)',
        link: {
            text: 'Go to Accounting - End-of-year checklist',
            url: 'https://eaccounting.vismaonline.com/#/accounting/fiscalyearchecklist',
        },
        sections: [
            {
                title: "How do I post this year's result?",
                body: "When you enter a new financial year, the year's result must be handled. The principle is the same regardless of company type, but the accounts used may differ.\n\nIf you use **Tax return & Annual report**, the result is posted automatically when the year is closed. Otherwise, you can post the result manually or use the accounting template **Profit/loss for the year**. [Read more here.](https://forum.spiris.se/t5/Tips-om-bokforing/Bokfora-arets-resultat/ta-p/176742)",
                questions: [
                    {
                        label: 'What must be completed before I lock the accounting year in Spiris?',
                        query: 'What must be completed before I lock the accounting year in Spiris?',
                    },
                    {
                        label: resultLabel,
                        query: resultQuery,
                    },
                    {
                        label: "What happens if I accidentally post the year's result incorrectly?",
                        query: "What happens if I accidentally post the year's result incorrectly?",
                    },
                ],
                link: {
                    text: 'Go to Accounting - Journal entries',
                    url: 'https://eaccounting.vismaonline.com/#/accounting/vouchers',
                },
            },
            {
                title: "How do I post last year's result?",
                body: "Check that last year's result has been transferred to equity. If it's missing, you can create a manual journal entry or use the accounting template **Reversal of profit/loss previous year** which guides you through the process. [Read more here.](https://forum.spiris.se/t5/Tips-om-bokforing/Bokfora-foregaende-ars-resultat/ta-p/176754)",
                questions: [
                    {
                        label: "How do I know if the year's result has been transferred to equity?",
                        query: "How do I know if the year's result has been transferred to equity in Spiris?",
                    },
                    {
                        label: 'Can I rebook the result even if the year is already locked?',
                        query: 'Can I rebook the result even if the year is already locked?',
                    },
                    {
                        label: 'What happens if I forget to transfer the result to equity?',
                        query: 'What happens if I forget to transfer the result to equity capital in the program?',
                    },
                ],
                link: {
                    text: 'Go to Accounting - Journal entries',
                    url: 'https://eaccounting.vismaonline.com/#/accounting/vouchers',
                },
            },
            {
                title: 'Working with two financial years at the same time?',
                body: 'You can work with two financial years at the same time until the previous year is closed. Transactions are posted to the year based on the transaction date. Bank transactions entered for the previous year automatically update the opening balance. [Read more here.](https://support.spiris.se/bokforing-fakturering/en-se/content/online-help/accounting-year-end-closing-checklist-se.htm)',
                questions: [
                    {
                        label: 'How do I handle revenue that is recorded this year but actually relates to the following year?',
                        query: 'In the program, how do I handle revenue that is recorded this year but actually relates to the following year?',
                    },
                    {
                        label: 'I sent an invoice in January that relates to work done in December. What do I do?',
                        query: 'I sent an invoice in January that relates to work done in December. What do I do?',
                    },
                    {
                        label: 'How do I lock an accounting year?',
                        query: 'How do I lock an accounting year?',
                    },
                ],
            },
            {
                title: 'When do I enter the opening balance?',
                body: 'You only enter the opening balance for the first financial year. When the year is closed, balances are automatically carried forward to the next year. [Read more here.](https://support.spiris.se/bokforing-fakturering/en-se/content/online-help/settings-financial-years-ob.htm)',
                questions: [
                    {
                        label: 'Can I import an SIE file to load my earlier accounting transactions?',
                        query: 'Can I import an SIE file to load my earlier accounting transactions?',
                    },
                ],
                link: {
                    text: 'Go to Settings - Financial Years and OB',
                    url: 'https://eaccounting.vismaonline.com/#/accounting/fiscalyears',
                },
            },
            {
                title: 'How do I create a new financial year?',
                body: 'You find the financial year settings under **Settings - Financial years and OB**. There you can create a new financial year by clicking **New financial year**. [Read more on Spiris Support.](https://support.spiris.se/bokforing-fakturering/en-se/content/faq/accounting-create-financial-year.htm)',
                questions: [
                    {
                        label: 'How do I lock an accounting year?',
                        query: 'How do I lock an accounting year?',
                    },
                    {
                        label: 'Do I have to close an accounting year before creating a new one?',
                        query: 'Do I have to close an accounting year before creating a new one?',
                    },
                    {
                        label: 'Can I import an SIE file to load my earlier accounting transactions?',
                        query: 'Can I import an SIE file to load my earlier accounting transactions?',
                    },
                ],
                link: {
                    text: 'Go to Settings - Financial Years and OB',
                    url: 'https://eaccounting.vismaonline.com/#/accounting/fiscalyears',
                },
            },
        ],
    };
};

const getSvEAccountingContent = (businessType: string): GuidanceCardProductContent => {
    let resultLabel = 'Hur bokför jag årets resultat?';
    let resultQuery = 'Bokföra årets resultat i Spiris';
    if (businessType === 'SoleProprietorship') {
        resultLabel = 'Hur bokför jag årets resultat för en enskild firma?';
        resultQuery = 'Bokföra årets resultat för en enskild firma i Spiris';
    } else if (businessType === 'Corporation') {
        resultLabel = 'Hur bokför jag årets resultat för ett aktiebolag?';
        resultQuery = 'Bokföra årets resultat för ett aktiebolag i Spiris';
    }

    return {
        title: 'Checklista för årsavstämning',
        description:
            'Nytt år och dags för många att stämma av och stänga bokföringsåret. Här följer några vanliga frågor.',
        link: {
            text: 'Gå till Bokföring - Årsavstämning',
            url: 'https://eaccounting.vismaonline.com/#/accounting/fiscalyearchecklist',
        },
        sections: [
            {
                title: 'Hur bokför jag årets resultat?',
                body: 'När det nya året börjar förs förra årets resultat (vinst/förlust) till eget kapital och det nya årets resultat nollställs. Principen är densamma oavsett företagsform, men konton kan skilja sig åt.\n\nAnvänder du **Deklaration & Årsredovisning** bokförs resultatet automatiskt när du låser året. Annars kan du bokföra resultatet manuellt eller använda bokföringsmallen **Bokföring av årets resultat**. [Läs mer här.](https://forum.spiris.se/t5/Tips-om-bokforing/Bokfora-arets-resultat/ta-p/176742)',
                questions: [
                    {
                        label: 'Vad måste vara klart innan jag låser bokföringsåret i Spiris?',
                        query: 'Vad måste vara klart innan jag låser bokföringsåret i Spiris?',
                    },
                    {
                        label: resultLabel,
                        query: resultQuery,
                    },
                    {
                        label: 'Vad händer om jag råkar bokföra årets resultat fel?',
                        query: 'Vad händer om jag råkar bokföra årets resultat fel i Spiris?',
                    },
                ],
                link: {
                    text: 'Gå till Bokföring - Verifikationer',
                    url: 'https://eaccounting.vismaonline.com/#/accounting/vouchers',
                },
            },
            {
                title: 'Hur bokför jag föregående års resultat?',
                body: 'Kontrollera att föregående års resultat har förts till eget kapital. Saknas det kan du skapa en manuell verifikation eller använda bokföringsmallen **Ombokning av fg års resultat** som guidar dig rätt. [Läs mer här](https://forum.spiris.se/t5/Tips-om-bokforing/Bokfora-foregaende-ars-resultat/ta-p/176754).',
                questions: [
                    {
                        label: 'Hur vet jag om årets resultat har flyttats till eget kapital?',
                        query: 'Hur vet jag om årets resultat har flyttats till eget kapital?',
                    },
                    {
                        label: 'Kan jag boka om resultatet även om året redan är låst?',
                        query: 'Kan jag boka om resultatet även om året redan är låst?',
                    },
                    {
                        label: 'Vad händer om jag glömmer att flytta resultatet till eget kapital?',
                        query: 'Vad händer om jag glömmer att flytta resultatet till eget kapital i programmet?',
                    },
                ],
                link: {
                    text: 'Gå till Bokföring - Verifikationer',
                    url: 'https://eaccounting.vismaonline.com/#/accounting/vouchers',
                },
            },
            {
                title: 'Arbeta med två år samtidigt?',
                body: 'Du kan arbeta parallellt med två räkenskapsår tills det föregående är avslutat. Händelser bokförs på det år som datumet avser. Bankhändelser som läggs in på föregående år uppdaterar automatiskt ingående balans. [Läs mer här](https://support.spiris.se/bokforing-fakturering/sv-se/content/online-help/accounting-year-end-closing-checklist-se.htm).',
                questions: [
                    {
                        label: 'Hur gör jag med intäkter som är bokförda i det här året men som egentligen avser kommande år?',
                        query: 'Hur gör jag i programmet med intäkter som är bokförda i det här året men som egentligen avser kommande år?',
                    },
                    {
                        label: 'Jag skickade en faktura i januari som avser arbete som gjorts i december. Vad gör jag?',
                        query: 'Jag skickade en faktura i januari som avser arbete som gjorts i december. Hur hanterar jag detta i programmet?',
                    },
                    {
                        label: 'Hur låser jag ett räkenskapsår?',
                        query: 'Hur låser jag ett räkenskapsår?',
                    },
                ],
            },
            {
                title: 'När lägger jag in ingående balans?',
                body: 'Du lägger bara in ingående balans för det första räkenskapsåret. När året avslutas förs balanserna automatiskt över till nästa år. [Läs mer här](https://support.spiris.se/bokforing-fakturering/sv-se/content/online-help/settings-financial-years-ob.htm).',
                questions: [
                    {
                        label: 'Kan jag importera en SIE-fil för att få med min tidigare bokföring?',
                        query: 'Kan jag importera en SIE-fil för att få med min tidigare bokföring?',
                    },
                ],
                link: {
                    text: 'Gå till Inställningar - Räkenskapsår och IB',
                    url: 'https://eaccounting.vismaonline.com/#/accounting/fiscalyears',
                },
            },
            {
                title: 'Hur skapar jag ett nytt år?',
                body: 'Du hittar inställningarna för räkenskapsår under **Inställningar - Räkenskapsår och IB**. Där kan du skapa ett nytt räkenskapsår genom att klicka på **Nytt räkenskapsår**. [Läs mer på Spiris Support](https://support.spiris.se/bokforing-fakturering/sv-se/content/faq/accounting-create-financial-year.htm).',
                questions: [
                    {
                        label: 'Hur låser jag ett räkenskapsår?',
                        query: 'Hur låser jag ett räkenskapsår?',
                    },
                    {
                        label: 'Måste jag stänga ett räkenskapsår innan jag skapar ett nytt?',
                        query: 'Måste jag stänga ett räkenskapsår i programmet innan jag skapar ett nytt?',
                    },
                    {
                        label: 'Kan jag importera en SIE-fil för att få med min tidigare bokföring?',
                        query: 'Kan jag importera en SIE-fil för att få med min tidigare bokföring?',
                    },
                ],
                link: {
                    text: 'Gå till Inställningar - Räkenskapsår och IB',
                    url: 'https://eaccounting.vismaonline.com/#/accounting/fiscalyears',
                },
            },
        ],
    };
};

const getEnSpeedledgerContent = (businessType: string): GuidanceCardProductContent => {
    const taxAndVatLink =
        businessType === 'SoleProprietorship' || businessType === 'GeneralPartnership'
            ? 'https://support.speedledger.se/hc/sv/articles/29925486664349-Bokf%C3%B6ra-skatt-och-moms-i-Enskild-firma-och-Handelsbolag'
            : 'https://support.speedledger.se/hc/sv/articles/202454049-Bokf%C3%B6ra-skatt-och-moms-Aktiebolag-och-f%C3%B6reningar';

    return {
        title: 'Checklist for Year-End Reconciliation',
        description:
            "A new year means it's time for many businesses to reconcile and close the accounting year. Below are some common questions: Tips for year-end [read more here.](https://support.speedledger.se/hc/sv/articles/203544899-Tips-inf%C3%B6r-%C3%A5rsskiftet-Generell)",
        sections: [
            {
                title: 'Accrue expenses and income between financial years',
                body: 'Accruals make it possible to allocate income and expenses to the correct financial year, for example prepaid income/expenses or costs and revenues earned in one year but paid in another. [Read more here.](https://support.speedledger.se/hc/sv/articles/360020005132-Periodiseringar)',
                questions: [
                    {
                        label: 'How do I record expenses that were paid this year but relate to next year?',
                        query: 'How do I record expenses that were paid this year but relate to next year?',
                    },
                    {
                        label: 'How do I record income that belongs to next year?',
                        query: 'How do I record income that belongs to next year?',
                    },
                    {
                        label: 'How do I record an expense or income that relates to this year but is paid next year?',
                        query: 'How do I record an expense or income that relates to this year but is paid next year?',
                    },
                ],
            },
            {
                title: 'Reconcile bank account',
                body: 'Reconciling a bank account means comparing the bank statement with the general ledger to ensure that the accounting matches the bank records. Balances and transactions are checked to identify errors, missing entries, or duplicate postings. [Read more here.](https://support.speedledger.se/hc/sv/articles/26166876806685-St%C3%A4mma-av-bankkonto)',
                questions: [
                    {
                        label: 'How do I reconcile the bank account with the accounting records?',
                        query: 'How do I reconcile the bank account with the accounting records?',
                    },
                    {
                        label: 'How do I find errors or missing bank transactions?',
                        query: 'How do I find errors or missing bank transactions?',
                    },
                    {
                        label: 'How do I verify that the balance matches the bank?',
                        query: 'How do I verify that the balance matches the bank?',
                    },
                ],
            },
            {
                title: 'Fixed assets and depreciation',
                body: 'Fixed assets are larger assets such as machinery, furnishings, or vehicles that are used over several years. The cost is allocated over time through depreciation so that the accounting reflects the decrease in value. [Read more here.](https://support.speedledger.se/hc/sv/articles/205872696-Inventarier-och-avskrivningar)',
                questions: [
                    {
                        label: 'How do I record the purchase of fixed assets?',
                        query: 'How do I record the purchase of fixed assets?',
                    },
                    {
                        label: 'How do I register depreciation on fixed assets?',
                        query: 'How do I register depreciation on fixed assets?',
                    },
                ],
            },
            {
                title: 'Record inventory / stock',
                body: 'If you hold inventory, you need to record its value as an asset in the year-end accounts and adjust it annually based on a physical inventory count. [Read more here.](https://support.speedledger.se/hc/sv/articles/207432156-Bokf%C3%B6ra-lager-varulager)',
                questions: [
                    {
                        label: 'How do I record purchases of goods for inventory?',
                        query: 'How do I record purchases of goods for inventory?',
                    },
                    {
                        label: 'How do I adjust the inventory value after stocktaking?',
                        query: 'How do I adjust the inventory value after stocktaking?',
                    },
                ],
            },
            {
                title: 'Record tax and VAT',
                body: `Here you will find guidance on how to record tax and VAT in your business, whether you operate a limited company/association or a sole trader/partnership. The guides explain how tax expenses and VAT should be correctly reported in the accounting based on your type of business. [Read more here.](${taxAndVatLink})`,
                questions: [
                    {
                        label: 'How do I record VAT on sales and purchases?',
                        query: 'How do I record VAT on sales and purchases?',
                    },
                    {
                        label: "How do I record the final tax on the year's result?",
                        query: "How do I record the final tax on the year's result?",
                    },
                    {
                        label: 'How do I record preliminary tax payments and VAT liabilities?',
                        query: 'How do I record preliminary tax payments and VAT liabilities?',
                    },
                ],
            },
        ],
    };
};

const getSvSpeedledgerContent = (businessType: string): GuidanceCardProductContent => {
    const taxAndVatLink =
        businessType === 'SoleProprietorship' || businessType === 'GeneralPartnership'
            ? 'https://support.speedledger.se/hc/sv/articles/29925486664349-Bokf%C3%B6ra-skatt-och-moms-i-Enskild-firma-och-Handelsbolag'
            : 'https://support.speedledger.se/hc/sv/articles/202454049-Bokf%C3%B6ra-skatt-och-moms-Aktiebolag-och-f%C3%B6reningar';

    return {
        title: 'Checklista för årsavstämning',
        description:
            'Nytt år och dags för många att stämma av och stänga bokföringsåret. Här följer några vanliga frågor. Tips inför årsskiftet: [Läs mer här.](https://support.speedledger.se/hc/sv/articles/203544899-Tips-inf%C3%B6r-%C3%A5rsskiftet-Generell)',
        sections: [
            {
                title: 'Periodisera kostnader och intäkter mellan år',
                body: 'Med periodiseringar kan man flytta intäkter och kostnader till rätt räkenskapsår. Tex. förutbetalda intäkter/Kostnader eller saker som upparbetats i ett år men betalas i ett annat. [Läs mer här.](https://support.speedledger.se/hc/sv/articles/360020005132-Periodiseringar)',
                questions: [
                    {
                        label: 'Hur bokför jag kostnader som betalats i år men avser nästa år?',
                        query: 'Hur bokför jag kostnader som betalats i år men avser nästa år?',
                    },
                    {
                        label: 'Hur bokför jag intäkter som hör till nästa år?',
                        query: 'Hur bokför jag intäkter som hör till nästa år?',
                    },
                    {
                        label: 'Hur bokför jag en kostnad eller intäkt som hör till detta året men betalas nästa?',
                        query: 'Hur bokför jag en kostnad eller intäkt som hör till detta året men betalas nästa?',
                    },
                ],
            },
            {
                title: 'Stämma av bankkonto',
                body: 'Att stämma av bankkonto innebär att jämföra kontoutdraget med huvudboken så att bokföringen stämmer mot banken. Saldon och händelser kontrolleras för att hitta fel, saknade eller dubbla poster. [Läs mer här.](https://support.speedledger.se/hc/sv/articles/26166876806685-St%C3%A4mma-av-bankkonto)',
                questions: [
                    {
                        label: 'Hur stämmer jag av bankkontot mot bokföringen?',
                        query: 'Hur stämmer jag av bankkontot mot bokföringen?',
                    },
                    {
                        label: 'Hur hittar jag fel eller saknade bankhändelser?',
                        query: 'Hur hittar jag fel eller saknade bankhändelser?',
                    },
                    {
                        label: 'Hur kontrollerar jag att saldot stämmer med banken?',
                        query: 'Hur kontrollerar jag att saldot stämmer med banken?',
                    },
                ],
            },
            {
                title: 'Inventarier och avskrivningar',
                body: 'Inventarier är större tillgångar som maskiner, inredning eller fordon som används under flera år. Kostnaden fördelas över tiden genom avskrivningar så att bokföringen speglar värdeminskningen. [Läs mer här.](https://support.speedledger.se/hc/sv/articles/205872696-Inventarier-och-avskrivningar)',
                questions: [
                    {
                        label: 'Hur bokför jag inköp av inventarier?',
                        query: 'Hur bokför jag inköp av inventarier?',
                    },
                    {
                        label: 'Hur registrerar jag avskrivningar på inventarier?',
                        query: 'Hur registrerar jag avskrivningar på inventarier?',
                    },
                ],
            },
            {
                title: 'Bokföra lager/ varulager',
                body: 'Om du har varor i lager behöver du bokföra lagrets värde som en tillgång i bokslutet och justera det årligen utifrån inventering. [Läs mer här.](https://support.speedledger.se/hc/sv/articles/207432156-Bokf%C3%B6ra-lager-varulager)',
                questions: [
                    {
                        label: 'Hur bokför jag inköp av varor till lagret?',
                        query: 'Hur bokför jag inköp av varor till lagret?',
                    },
                    {
                        label: 'Hur justerar jag lagervärdet vid inventering?',
                        query: 'Hur justerar jag lagervärdet vid inventering?',
                    },
                ],
            },
            {
                title: 'Bokföra skatt och moms',
                body: `Här hittar du vägledning för hur du bokför skatt och moms i din verksamhet, oavsett om du har ett aktiebolag/förening eller en enskild firma/handelsbolag. Guiderna förklarar hur skattekostnaden och momsen ska redovisas rätt i bokföringen utifrån din bolagsform. [Läs mer här.](${taxAndVatLink})`,
                questions: [
                    {
                        label: 'Hur bokför jag moms på försäljning och inköp?',
                        query: 'Hur bokför jag moms på försäljning och inköp?',
                    },
                    {
                        label: 'Hur bokför jag slutlig skatt på årets resultat?',
                        query: 'Hur bokför jag slutlig skatt på årets resultat?',
                    },
                    {
                        label: 'Hur bokför jag preliminär skatt och momsskulder?',
                        query: 'Hur bokför jag preliminär skatt och momsskulder?',
                    },
                ],
            },
        ],
    };
};

// A dictionary mapping product names to their specific content
export const guidanceCardContent: Record<string, Record<string, GuidanceCardContentMap>> = {
    en: {
        [ProductConstants.EAccounting.ProductName]: {
            default: getEnEAccountingContent('default'),
            SoleProprietorship: getEnEAccountingContent('SoleProprietorship'),
            Corporation: getEnEAccountingContent('Corporation'),
        },
        [ProductConstants.Speedledger.ProductName]: {
            default: getEnSpeedledgerContent('default'),
            SoleProprietorship: getEnSpeedledgerContent('SoleProprietorship'),
            GeneralPartnership: getEnSpeedledgerContent('GeneralPartnership'),
            Corporation: getEnSpeedledgerContent('Corporation'),
        },
    },
    sv: {
        [ProductConstants.EAccounting.ProductName]: {
            default: getSvEAccountingContent('default'),
            SoleProprietorship: getSvEAccountingContent('SoleProprietorship'),
            Corporation: getSvEAccountingContent('Corporation'),
        },
        [ProductConstants.Speedledger.ProductName]: {
            default: getSvSpeedledgerContent('default'),
            SoleProprietorship: getSvSpeedledgerContent('SoleProprietorship'),
            GeneralPartnership: getSvSpeedledgerContent('GeneralPartnership'),
            Corporation: getSvSpeedledgerContent('Corporation'),
        },
    },
};
